import { useState } from "react";
import {
  CaretDown, CaretUp, CheckCircle, Warning, XCircle, ArrowClockwise,
  Lightning, ThermometerHot, ShieldCheck, Waveform, SpeakerHigh, BookOpen,
} from "@phosphor-icons/react";

const DOMAIN_ICONS = {
  electromagnetic: Lightning, thermal: ThermometerHot, dielectric: ShieldCheck,
  short_circuit: Waveform, noise: SpeakerHigh, standards_compliance: BookOpen,
};
const STATUS = {
  pass: { label: "PASS", color: "var(--c-green)", Icon: CheckCircle },
  warnings: { label: "WARNINGS", color: "var(--c-amber)", Icon: Warning },
  fail: { label: "FAIL", color: "var(--c-red)", Icon: XCircle },
};
const SEV_COLOR = { critical: "var(--c-red)", major: "var(--c-amber)", minor: "var(--c-accent)", advisory: "var(--c-muted)" };

function DomainFindings({ domainId, issues, status }) {
  const open = issues.filter((i) => i.status !== "waived");
  const waived = issues.filter((i) => i.status === "waived");
  if (!open.length && !waived.length) {
    return (
      <div className="mb-3 flex items-center gap-2 text-xs text-[var(--c-green)]" data-testid={`domain-findings-${domainId}`}>
        <CheckCircle size={13} weight="fill" /> All checks pass in this domain.
      </div>
    );
  }
  return (
    <div className="mb-3 space-y-1.5" data-testid={`domain-findings-${domainId}`}>
      {open.length > 0 && (
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">
          Why it {status === "fail" ? "fails" : "shows warnings"} — {open.length} open finding{open.length > 1 ? "s" : ""}
        </div>
      )}
      {open.map((i) => (
        <div key={i.id} className="bg-[var(--c-bg)] border rounded-sm px-3 py-2"
          style={{ borderColor: `color-mix(in srgb, ${SEV_COLOR[i.severity] || "var(--c-border)"} 45%, transparent)` }}
          data-testid={`finding-${i.check_id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: SEV_COLOR[i.severity] || "var(--c-muted)" }} />
            <span className="text-xs text-[var(--c-text)] font-medium">{i.title}</span>
            <span className="font-mono text-[9px] text-[var(--c-muted)]">{i.check_id}</span>
            <span className="font-mono text-[10px] ml-auto">
              <span className="text-[var(--c-amber)]">{i.calculated}</span>
              <span className="text-[var(--c-muted)]"> vs {i.required}</span>
            </span>
          </div>
          {i.description && <div className="text-[11px] text-[var(--c-muted)] mt-1 leading-relaxed">{i.description}</div>}
          {i.client_rule && <div className="text-[10px] text-[var(--c-accent)] mt-1 font-mono">Client rule applied: {i.client_rule}</div>}
        </div>
      ))}
      {waived.length > 0 && (
        <div className="text-[10px] font-mono text-[var(--c-muted)]" data-testid={`findings-waived-${domainId}`}>
          {waived.length} finding(s) waived: {waived.map((i) => i.check_id).join(", ")}
        </div>
      )}
    </div>
  );
}

function MiniTable({ head, rows, testid, okKey }) {
  return (
    <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm overflow-x-auto" data-testid={testid}>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[var(--c-border)]">
            {head.map((h) => <th key={h} className="text-left px-3 py-2 text-[9px] font-mono tracking-widest uppercase text-[var(--c-muted)] whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[var(--c-border)]/50 last:border-0">
              {r.map((c, j) => (
                <td key={j} className={`px-3 py-2 whitespace-nowrap ${j === 0 ? "text-[var(--c-text)]" : "font-mono text-[var(--c-muted)]"}`}>
                  {okKey && j === r.length - 1
                    ? <span className={c ? "text-[var(--c-green)]" : "text-[var(--c-red)]"}>{c ? "PASS" : "FAIL"}</span>
                    : c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Gauge({ gauge }) {
  const pct = Math.min(gauge.value / gauge.max, 1) * 100;
  const limPct = (gauge.limit / gauge.max) * 100;
  const over = gauge.value > gauge.limit;
  return (
    <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm p-4" data-testid="thermal-gauge">
      <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Top-oil rise gauge</div>
      <div className="relative h-4 bg-[var(--c-border)] rounded-sm">
        <div className="h-full rounded-sm transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: over ? "var(--c-red)" : "var(--c-green)" }} />
        <div className="absolute top-[-4px] bottom-[-4px] w-px bg-[var(--c-amber)]" style={{ left: `${limPct}%` }} />
      </div>
      <div className="flex justify-between font-mono text-[10px] mt-1.5">
        <span className="text-[var(--c-muted)]">0</span>
        <span className={over ? "text-[var(--c-red)]" : "text-[var(--c-green)]"}>{gauge.value} K</span>
        <span className="text-[var(--c-amber)]">limit {gauge.limit} K</span>
        <span className="text-[var(--c-muted)]">{gauge.max} K</span>
      </div>
    </div>
  );
}

function OverloadChart({ curve }) {
  const W = 300, H = 150, px = 36, py = 20;
  const lMin = 100, lMax = 160;
  const dMax = Math.max(...curve.map((c) => c.duration_min));
  const X = (l) => px + ((l - lMin) / (lMax - lMin)) * (W - px - 12);
  const Y = (d) => H - py - (d / dMax) * (H - py - 14);
  const pts = curve.map((c) => `${X(c.load_pct)},${Y(c.duration_min)}`).join(" ");
  return (
    <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm p-3" data-testid="overload-chart">
      <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-1">Overload capability (IEC 60076-7)</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="max-w-full font-mono">
        <line x1={px} y1={H - py} x2={W - 8} y2={H - py} style={{ stroke: "var(--c-border)" }} />
        <line x1={px} y1={8} x2={px} y2={H - py} style={{ stroke: "var(--c-border)" }} />
        <polyline points={pts} fill="none" style={{ stroke: "var(--c-accent)" }} strokeWidth="1.4" />
        {curve.map((c) => (
          <g key={c.load_pct}>
            <circle cx={X(c.load_pct)} cy={Y(c.duration_min)} r="3" style={{ fill: "var(--c-amber)" }} />
            <text x={X(c.load_pct)} y={Y(c.duration_min) - 7} fontSize="8" style={{ fill: "var(--c-muted)" }} textAnchor="middle">{c.duration_min}m</text>
            <text x={X(c.load_pct)} y={H - 7} fontSize="8" style={{ fill: "var(--c-muted)" }} textAnchor="middle">{c.load_pct}%</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function Bars({ items, valueKey, labelKey, unit, limitValue, testid }) {
  const max = Math.max(...items.map((i) => i[valueKey]), limitValue || 0, 1);
  return (
    <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm p-4" data-testid={testid}>
      {items.map((i) => (
        <div key={i[labelKey]} className="flex items-center gap-2 mb-2">
          <span className="text-[10px] text-[var(--c-muted)] w-32 shrink-0">{i[labelKey]}</span>
          <div className="flex-1 h-3 bg-[var(--c-border)] rounded-sm overflow-hidden relative">
            <div className="h-full rounded-sm"
              style={{ width: `${(i[valueKey] / max) * 100}%`, backgroundColor: limitValue && i[valueKey] > limitValue ? "var(--c-red)" : "var(--c-accent)" }} />
            {limitValue != null && (
              <div className="absolute top-[-2px] bottom-[-2px] w-px bg-[var(--c-amber)]" style={{ left: `${(limitValue / max) * 100}%` }} />
            )}
          </div>
          <span className="font-mono text-[10px] text-[var(--c-text)] w-20 text-right">{i[valueKey]} {unit}</span>
        </div>
      ))}
    </div>
  );
}

function DomainDetail({ id, data }) {
  if (!data) return null;
  if (id === "electromagnetic") {
    return (
      <div className="space-y-3">
        <MiniTable testid="em-taps-table" head={["Tap %", "HV turns", "Ratio error %", "Check"]} okKey
          rows={(data.tap_rows || []).map((t) => [`${t.tap_pct > 0 ? "+" : ""}${t.tap_pct}%`, t.hv_turns, t.error_pct, t.ok])} />
        <MiniTable testid="em-rows" head={["Parameter", "Value"]} rows={data.rows || []} />
      </div>
    );
  }
  if (id === "thermal") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.gauge && <Gauge gauge={data.gauge} />}
          {data.overload_curve && <OverloadChart curve={data.overload_curve} />}
        </div>
        {data.profile_sim && (
          <div className={`border rounded-sm px-3 py-2 text-xs ${data.profile_sim.ok ? "border-[var(--c-green)]/40 bg-[var(--c-green)]/10" : "border-[var(--c-red)]/40 bg-[var(--c-red)]/10"}`}
            data-testid="profile-sim">
            Client loading profile ({data.profile_sim.profile}): peak hot spot <span className="font-mono">{data.profile_sim.peak_hot_spot_c} °C</span> vs limit <span className="font-mono">{data.profile_sim.limit_c} °C</span> — {data.profile_sim.ok ? "OK" : "EXCEEDED"}
          </div>
        )}
        <MiniTable testid="thermal-rows" head={["Parameter", "Value"]} rows={data.rows || []} />
      </div>
    );
  }
  if (id === "dielectric") {
    return (
      <div className="space-y-3">
        <MiniTable testid="bil-coord-table" head={["Winding", "Um kV", "BIL req kVp", "BIL spec kVp", "SIL kVp", "Check"]} okKey
          rows={(data.coord || []).map((c) => [c.winding, c.um_kv, c.bil_required, c.bil_specified ?? "—", c.sil ?? "—", c.ok])} />
        <MiniTable testid="clearance-table" head={["Location", "Type", "Required mm", "Actual mm", "Check"]} okKey
          rows={(data.clear_rows || []).map((r) => [r.location, r.type, r.required, r.actual, r.actual >= r.required])} />
        {data.altitude_m > 1000 && (
          <div className="text-[11px] text-[var(--c-amber)] font-mono" data-testid="altitude-note">
            Altitude {data.altitude_m} m → IEC 60076-3 correction factor ka = {data.ka} applied to required clearances
          </div>
        )}
        <MiniTable testid="dielectric-rows" head={["Check", "Value"]}
          rows={[["Oil-gap stress (HV–LV duct)", `${data.oil_gap_stress} kV/mm (≤ 6)`],
                 ["AC test voltage", `${data.ac_test?.specified ?? "default"} / required ${data.ac_test?.required} kV`]]} />
      </div>
    );
  }
  if (id === "short_circuit") {
    return (
      <div className="space-y-3">
        <Bars testid="axial-bars" items={(data.axial || []).map((a) => ({ ...a }))} labelKey="winding" valueKey="force_kn" unit="kN" limitValue={data.clamp_capacity_kn} />
        <MiniTable testid="sc-taps-table" head={["Tap %", "Z %", "Isc HV kA", "Isc LV kA"]}
          rows={(data.taps_tbl || []).map((t) => [`${t.tap_pct > 0 ? "+" : ""}${t.tap_pct}%`, t.z_pct, t.i_sc_hv_ka, t.i_sc_lv_ka])} />
        <MiniTable testid="sc-rows" head={["Parameter", "Value"]} rows={data.rows || []} />
      </div>
    );
  }
  if (id === "noise") {
    return <Bars testid="noise-bars" items={data.bars || []} labelKey="label" valueKey="db" unit="dB(A)" />;
  }
  if (id === "standards_compliance") {
    return (
      <MiniTable testid="standards-matrix" head={["Standard", "Clause", "Parameter", "Required", "Actual", "Tolerance", "Status"]} okKey
        rows={(data.matrix || []).map((r) => [r.standard, r.clause, r.parameter, r.required, r.actual, r.tolerance, r.status === "pass"])} />
    );
  }
  if (id === "spec_compliance") {
    return (
      <div className="space-y-2">
        <div className="text-[10px] font-mono text-[var(--c-muted)]">
          Template: {data.template_name || "—"} · {data.checked || 0} stated value(s) checked · {data.deviations || 0} deviation(s)
        </div>
        <MiniTable testid="spec-compliance-rows" head={["Parameter", "Specified", "In spec sheet", "Designed", "State"]}
          rows={(data.rows || []).map((r) => [r.label, `${r.stated ?? "—"} ${r.unit || ""}`.trim(),
            r.current ?? "—", r.designed ?? "—", r.state])} />
      </div>
    );
  }
  return null;
}

export default function DomainCard({ meta, result, running, onRun }) {
  const [open, setOpen] = useState(false);
  const Icon = DOMAIN_ICONS[meta.id] || BookOpen;
  const st = result ? STATUS[result.status] : null;
  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm" data-testid={`domain-card-${meta.id}`}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-sm bg-[var(--c-bg)] border border-[var(--c-border)] flex items-center justify-center shrink-0">
            <Icon size={16} className="text-[var(--c-accent)]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-[var(--c-text)]">{meta.name}</span>
              {st && (
                <span className="inline-flex items-center gap-1 text-[9px] font-mono tracking-wider px-1.5 py-0.5 rounded-sm border"
                  style={{ color: st.color, borderColor: `color-mix(in srgb, ${st.color} 40%, transparent)`, backgroundColor: `color-mix(in srgb, ${st.color} 8%, transparent)` }}
                  data-testid={`domain-status-${meta.id}`}>
                  <st.Icon size={10} weight="bold" /> {st.label}
                </span>
              )}
              {!meta.registered && <span className="text-[9px] font-mono text-[var(--c-muted)]">NOT REGISTERED</span>}
            </div>
            <div className="text-[10px] text-[var(--c-muted)] mt-0.5 truncate">{meta.desc}</div>
          </div>
          <button onClick={() => onRun(meta.id)} disabled={running || !meta.registered}
            data-testid={`run-domain-${meta.id}`} title="Run this domain"
            className="text-[var(--c-muted)] hover:text-[var(--c-accent)] disabled:opacity-40 transition-colors duration-150">
            <ArrowClockwise size={15} className={running ? "animate-spin" : ""} />
          </button>
        </div>
        {result && (
          <div className="mt-3">
            <div className="flex justify-between text-[10px] font-mono mb-1">
              <span className="text-[var(--c-muted)]">{result.key_metric.label}: <span className="text-[var(--c-text)]">{result.key_metric.value}</span></span>
              <span style={{ color: st.color }}>{result.score}/100</span>
            </div>
            <div className="h-1.5 bg-[var(--c-border)] rounded-sm overflow-hidden">
              <div className="h-full rounded-sm transition-all duration-500" style={{ width: `${result.score}%`, backgroundColor: st.color }} />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-[10px] font-mono text-[var(--c-muted)]">
                {result.issues.filter((i) => i.status !== "waived").length} open · {result.issues.filter((i) => i.status === "waived").length} waived
              </span>
              <button onClick={() => setOpen((o) => !o)} data-testid={`domain-expand-${meta.id}`}
                className="flex items-center gap-1 text-[10px] text-[var(--c-accent)] hover:text-[var(--c-text)] transition-colors duration-150">
                Detail {open ? <CaretUp size={11} /> : <CaretDown size={11} />}
              </button>
            </div>
          </div>
        )}
        {!result && meta.registered && <div className="text-[10px] text-[var(--c-muted)] mt-3 font-mono">Not run yet</div>}
      </div>
      {open && result && (
        <div className="border-t border-[var(--c-border)] p-4" data-testid={`domain-detail-${meta.id}`}>
          <DomainFindings domainId={meta.id} issues={result.issues || []} status={result.status} />
          <DomainDetail id={meta.id} data={result.data} />
        </div>
      )}
    </div>
  );
}
